import { useState } from "react";
import { Icon } from "../components/Icon";
import { ProgressBar } from "../components/ProgressBar";
import { median } from "../lib/engine.mjs";
import { exposureTotals } from "../lib/exposure.mjs";
import { useLanguage } from "../app/useStore";
import { trackCounts } from "../lib/summary";

type AnswerMode = "translation" | "target";

export function StatsPage() {
  const ctx = useLanguage();
  const { progress: p, definition: d, deck, stories, lessons } = ctx;
  const [type, setType] = useState<"words" | "characters">("words");
  const [query, setQuery] = useState("");
  const [answerMode, setAnswerMode] = useState<AnswerMode>(p.prefs.english ? "translation" : "target");

  const counts = trackCounts(ctx);
  const guided = ctx.programs.filter((x) => x.lessonId);
  const sessions = p.sessions.filter((s) => s.answerMode === answerMode);
  const recent = sessions.slice(-5);
  const accuracy = recent.length ? Math.round(recent.reduce((n, s) => n + s.accuracy, 0) / recent.length) : null;
  const time = median(recent.map((s) => s.time).filter((t) => typeof t === "number"));
  const competitive = sessions.filter((s) => s.type === "sprint" && !s.focused && s.count === 20);
  const best = competitive.length ? Math.round(Math.max(...competitive.map((s) => s.score))) : null;
  const totals = deck.reduce((sum, w) => {
    const t = exposureTotals(p.exposure.words[w.id]);
    return { audio: sum.audio + t.audio, reading: sum.reading + t.reading, reviews: sum.reviews + t.reviews };
  }, { audio: 0, reading: 0, reviews: 0 });
  const encountered = deck.filter((w) => exposureTotals(p.exposure.words[w.id]).score > 0).length;
  const due = deck.filter((w) => p.records[answerMode][w.id]?.due <= Date.now()).length;

  const rows = type === "words"
    ? deck.map((w) => ({ id: w.id, label: w.word, reading: w.reading, meaning: w.meaning, row: p.exposure.words[w.id] }))
    : Object.entries(p.exposure.characters as Record<string, any>)
        .map(([id, row]) => ({ id, label: id, reading: undefined as string | undefined, meaning: undefined as string | undefined, row }))
        .sort((a, b) => exposureTotals(b.row).score - exposureTotals(a.row).score);
  const q = query.trim().toLowerCase();
  const shown = rows.filter((r) => [r.label, r.reading, r.meaning].some((v) => (v || "").toLowerCase().includes(q)));

  const trackRows: [string, any, number, number, string][] = [
    ["Study", "study", counts.study, lessons.length, "lessons reviewed"],
    ["Audio", "audio", counts.audio, guided.length || lessons.length, "guided lessons listened"],
    ["Stories", "stories", counts.stories, stories.length, "stories listened"],
  ];

  return (
    <>
      <div className="view-heading">
        <div>
          <p className="overline">{d.name} progress</p>
          <h1>Every encounter counts.</h1>
          <p>A picture of what you’ve heard, reviewed, and understood.</p>
        </div>
        <span className="count-badge">Saved on this device</span>
      </div>
      <div className="metrics-strip">
        <div><span>Listening time</span><strong id="listening-minutes">{(p.listeningSeconds / 60).toFixed(1)}<small>min</small></strong>
          <p>Since {new Date(p.listeningSince).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p></div>
        <div><span>Words encountered</span><strong>{encountered}<small>/ {deck.length}</small></strong><p>Across your learning tracks</p></div>
        <div><span>Audio appearances</span><strong>{totals.audio.toLocaleString()}</strong><p>Completed word encounters</p></div>
        <div><span>Reading appearances</span><strong>{totals.reading.toLocaleString()}</strong><p>Confirmed reading and review</p></div>
      </div>

      <div className="section-title"><h2>Your tracks</h2></div>
      <div className="track-progress-grid">
        {trackRows.map(([label, page, count, total, unit]) => (
          <a key={label} href={ctx.href(page)}>
            <strong>{label}</strong><span>{count} / {total} {unit}</span>
            <ProgressBar done={count} total={total} label={label + " progress"} />
          </a>
        ))}
      </div>

      <section className="practice-stats">
        <div className="section-title">
          <h2>Recognition practice</h2>
          <div className="segmented">
            <button className={answerMode === "translation" ? "active" : ""} onClick={() => setAnswerMode("translation")}>Translation answers</button>
            <button className={answerMode === "target" ? "active" : ""} onClick={() => setAnswerMode("target")}>{d.name} answers</button>
          </div>
        </div>
        <div className="summary-metrics">
          <div><strong>{accuracy === null ? "—" : accuracy + "%"}</strong><span>Recent accuracy</span></div>
          <div><strong>{time === null ? "—" : time.toFixed(1) + "s"}</strong><span>Median correct time</span></div>
          <div><strong>{best === null ? "—" : best}</strong><span>Best full-deck round / 100</span></div>
          <div><strong>{due}</strong><span>Due for review</span></div>
        </div>
        <p className="hint">Recent results use your last five rounds in this answer mode. Listening and lesson completion do not award mastery.</p>
      </section>

      <div className="section-title">
        <h2>Your encounters</h2>
        <div className="segmented">
          <button className={type === "words" ? "active" : ""} onClick={() => { setType("words"); setQuery(""); }}>Words</button>
          {d.capabilities.characters && <button className={type === "characters" ? "active" : ""} onClick={() => { setType("characters"); setQuery(""); }}>Characters</button>}
        </div>
      </div>
      <input id="stats-search" className="search-input" type="search" aria-label="Search exposure stats" value={query}
        placeholder={`Find a word${d.capabilities.characters ? " or character" : ""}…`} onChange={(e) => setQuery(e.target.value)} />
      <div className="exposure-table">
        <div className="exposure-table-head"><span>{type === "words" ? "Word" : "Character"}</span><span>Heard</span><span>Read</span><span>Reviews</span><span>Exposure</span></div>
        <div id="exposure-rows">
          {shown.map((r) => {
            const t = exposureTotals(r.row);
            return (
              <button key={r.id} className="exposure-row" onClick={() => (type === "words" ? ctx.openWord(r.id) : ctx.openCharacter(r.id))}>
                <span><strong lang={d.id}>{r.label}</strong>{r.meaning && <small>{p.prefs.english ? r.meaning : r.reading}</small>}</span>
                <span>{t.audio}</span><span>{t.reading}</span><span>{t.reviews}</span><span>{t.score}</span>
              </button>
            );
          })}
          {!shown.length && <p className="empty-state">No encounters here yet. Listen or study to begin.</p>}
        </div>
      </div>
      <p className="footnote">Exposure is audio plus reading appearances, not a proficiency grade. Earlier progress is preserved; listening minutes are measured from this update onward.</p>

      <div className="section-title"><h2>Story comprehension</h2></div>
      <div className="story-stats-list">
        {stories.map((s) => {
          const st = p.exposure.stories[s.id] || {};
          const last = st.results?.at(-1);
          return (
            <a key={s.id} href={ctx.href("stories", s.id)}>
              <div><strong lang={d.id}>{s.title}</strong><span>{s.titleEn}</span></div>
              <span>{st.plays || 0} listens</span>
              <div><strong>{last ? `${last.correct}/${last.total}` : "—"}</strong><span>{last ? (last.assisted ? "English help" : "Without subtitles") : "No check yet"}</span></div>
              <Icon name="next" />
            </a>
          );
        })}
      </div>
    </>
  );
}
